import type { Property } from '~/core/entities/property';

import { EventBusKeys } from '~/core/helpers/symbols';
import { MixpanelProps } from '~/core/service/mixpanel';
import { grammarInflection } from '~~/utils';

import { useEvents } from './events/useEvents';

interface UsePropertyPhotoCounterOptions {
	property: Ref<Property | null>;
}

export function usePropertyPhotoCounter({
	property
}: UsePropertyPhotoCounterOptions) {
	const { trackPropertyImageScrolled } = useEvents();
	const clickCount = ref<number>(0);

	const desktopCarouselCount = ref<number | undefined>(undefined);
	useEventBus<number | undefined>(EventBusKeys.desktopHeroSectionPhotoCount).on(
		count => {
			desktopCarouselCount.value = count;
		}
	);

	const mobileCarouselCount = ref<number | undefined>(undefined);
	useEventBus<number | undefined>(EventBusKeys.mobileHeroSectionPhotoCount).on(
		count => {
			mobileCarouselCount.value = count;
		}
	);

	watch(desktopCarouselCount, (newCount, oldCount) => {
		if (oldCount !== undefined && newCount !== oldCount) {
			clickCount.value = clickCount.value + 1;

			trackPropertyImageScrolled({
				[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
				[MixpanelProps.CURRENT_PAGE]: 'Property Details',
				[MixpanelProps.SECTION_NAME]: 'hero section',
				[MixpanelProps.CARD_POSITION]: newCount,
				[MixpanelProps.CARD_TYPE]: 'regular_card',
				[MixpanelProps.COMPANY_NAME]: property.value?.company.name!,
				[MixpanelProps.TAG_EXCLUSIVE]: property.value?.isExclusive!,
				[MixpanelProps.AGENT_NAME]: property.value?.agent.name!,
				[MixpanelProps.CLICK_COUNT]: clickCount.value
			});
		}
	});

	watch(mobileCarouselCount, (newCount, oldCount) => {
		if (oldCount !== undefined && newCount !== oldCount) {
			clickCount.value = clickCount.value + 1;
			trackPropertyImageScrolled({
				[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
				[MixpanelProps.CURRENT_PAGE]: 'Property Details',
				[MixpanelProps.SECTION_NAME]: 'hero section',
				[MixpanelProps.CARD_POSITION]: newCount,
				[MixpanelProps.CARD_TYPE]: 'regular_card',
				[MixpanelProps.COMPANY_NAME]: property.value?.company.name!,
				[MixpanelProps.TAG_EXCLUSIVE]: property.value?.isExclusive!,
				[MixpanelProps.AGENT_NAME]: property.value?.agent.name!,
				[MixpanelProps.CLICK_COUNT]: clickCount.value
			});
		}
	});

	const numberOfImages = computed<number>(
		() => property.value?.images.length ?? 0
	);

	const numberOfVideos = computed<number>(
		() => property.value?.videos.length ?? 0
	);

	// SSR-safe: use default values that match between server and client
	const desktopPhotoModalCtaText = computed<string | undefined>(() => {
		const totalPhotos = numberOfImages.value;

		if (!totalPhotos) {
			return 'Fotos';
		}

		const label = grammarInflection(totalPhotos, 'foto', 'fotos');

		// Use carousel count if available, otherwise use SSR-safe default
		const defaultCount = totalPhotos >= 4 ? 4 : totalPhotos;
		const currentPhotoCount = desktopCarouselCount.value ?? defaultCount;

		return `${currentPhotoCount}/${totalPhotos} ${label}`;
	});

	const mobilePhotoModalCtaText = computed<string | undefined>(() => {
		const totalPhotos = numberOfImages.value;
		const label = grammarInflection(totalPhotos, 'foto', 'fotos');

		// Use carousel count if available, otherwise use SSR-safe default (1)
		const currentPhotoCount = mobileCarouselCount.value ?? 1;

		return `${currentPhotoCount}/${totalPhotos} ${label}`;
	});

	return {
		numberOfImages,
		numberOfVideos,
		desktopPhotoModalCtaText,
		mobilePhotoModalCtaText
	};
}
